import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { pdfjs } from "react-pdf";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
  responsiveFontSizes,
  THEME_ID as MATERIAL_THEME_ID,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { FinanceableThemeProvider } from "@financeable-com-au/financeable-ui";
import { SnackbarProvider } from "notistack";
import { Snackbar } from "@/components/Snackbar";
import { LodgeAIProvider } from "@/providers/LodgeAIContextProvider";
import { fetchCsrfToken, setWorkOsAuthenticated } from "@/utils/useFetch";
import { getTheme } from "@/store/slices/themeSlice";
import { authV2Selector } from "@/store/slices/authV2Slice";
// @ts-expect-error: ignoring as it is existing js file
import Router from "./routes";
import IntercomChat from "./components/IntercomChat";

// Text layer for React-PDF.
import "react-pdf/dist/Page/TextLayer.css";
import colors from "./style/colors";
import { useCustomThemePalette } from "./hooks";

// Importing the PDF.js worker.
pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

// ----------------------------------------------------------------------
export default function App() {
  const dispatch = useDispatch();
  const { session } = useSelector(authV2Selector);
  const isAuthenticated = session.authenticated || Boolean(sessionStorage.token);
  // Fetch the organisation's custom theme from the server on app mount.
  // The result is stored in Redux and consumed by useCustomThemePalette, which
  // falls back to hardcoded colours if the fetch hasn't resolved yet.
  useEffect(() => {
    dispatch(getTheme());
  }, [dispatch]);
  const customPalette = useCustomThemePalette();

  // Mirror the WorkOS auth flag into useFetch so non-React callers
  // (waitForCsrfToken) can gate the CSRF fetch without reading Redux.
  useEffect(() => {
    setWorkOsAuthenticated(session.authenticated);
  }, [session.authenticated]);

  // Fetch CSRF token once the user is authenticated, and re-fetch when the tab
  // becomes visible. Re-fetching on visibilitychange handles cross-tab logout/login:
  // if a user logs out in tab B and logs back in, tab A's stored CSRF token is
  // stale. When the user switches back to tab A, the token is refreshed before
  // any request fires. fetchCsrfToken has built-in deduplication via its
  // module-level promise, so rapid tab switches won't cause duplicate requests.
  useEffect(() => {
    const sessionCsrfToken = sessionStorage.getItem("csrfToken");
    if (isAuthenticated && !sessionCsrfToken) {
      fetchCsrfToken();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isAuthenticated) {
        fetchCsrfToken();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isAuthenticated]);

  const theme = useMemo(() => {
    const financeableMUITheme = createTheme({
      palette: {
        type: "light",
        primary: {
          main: customPalette ? customPalette.primary["200"] : "#6A0BE3",
        },
        secondary: {
          main: colors.secondary,
        },
        warning: {
          main: customPalette ? customPalette.warning["200"] : colors.mediumOrange,
        },
        success: {
          main: customPalette ? customPalette.success["200"] : "#229A16",
        },
        error: {
          main: customPalette ? customPalette.danger["200"] : "#D32F2F",
        },
        info: {
          main: "#1890FF",
        },
        archive: {
          main: customPalette ? customPalette.neutral["800"] : colors.mediumOrange,
        },
        unarchive: {
          main: customPalette ? customPalette.neutral["800"] : colors.mediumOrange,
        },
        background: {
          default: "#ffffff",
          paper: "#ffffff",
        },
        text: {
          // @ts-expect-error: ignoring as it is existing setting
          white: colors.white,
          black: colors.black,
        },
      },
      typography: {
        body1: {
          letterSpacing: "0.5px",
        },
        h1: {
          fontFamily: [
            "Cabin",
            "-apple-system",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            '"Helvetica Neue"',
            "Arial",
            "sans-serif",
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
          ].join(","),
        },
        h2: {
          fontFamily: [
            "Cabin",
            "-apple-system",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            '"Helvetica Neue"',
            "Arial",
            "sans-serif",
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
          ].join(","),
        },
        h3: {
          fontFamily: [
            "Cabin",
            "-apple-system",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            '"Helvetica Neue"',
            "Arial",
            "sans-serif",
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
          ].join(","),
        },
        h4: {
          fontFamily: [
            "Cabin",
            "-apple-system",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            '"Helvetica Neue"',
            "Arial",
            "sans-serif",
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
          ].join(","),
        },
        h5: {
          fontFamily: [
            "Cabin",
            "-apple-system",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            '"Helvetica Neue"',
            "Arial",
            "sans-serif",
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
          ].join(","),
        },
        h6: {
          fontFamily: [
            "Cabin",
            "-apple-system",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            '"Helvetica Neue"',
            "Arial",
            "sans-serif",
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
          ].join(","),
        },
        fontFamily: [
          "-apple-system",
          "BlinkMacSystemFont",
          '"Segoe UI"',
          "Roboto",
          '"Helvetica Neue"',
          "Arial",
          "sans-serif",
          '"Apple Color Emoji"',
          '"Segoe UI Emoji"',
          '"Segoe UI Symbol"',
        ].join(","),
      },
      components: {
        MuiAccordion: {
          styleOverrides: {
            root: {
              boxShadow: "none",
              background: "none",
            },
          },
        },
        MuiAccordionSummary: {
          styleOverrides: {
            root: {
              "&$expanded": {
                minHeight: 0,
              },
              margin: 0,
            },
            expanded: {},
            content: {
              "&$expanded": {
                margin: 0,
                minHeight: 0,
              },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              backgroundColor: "#ffffff",
            },
          },
        },
      },
    });
    return responsiveFontSizes(financeableMUITheme);
  }, [customPalette]);

  // Memoise the themed provider tree so unrelated App re-renders (e.g. auth/session
  // changes) don't cascade into it. FinanceableThemeProvider rebuilds the Joy theme
  // and re-emits its `display: contents` wrapper div (with a fresh inline-style object)
  // on every render. That DOM churn makes Stripe.js's fraud MutationObserver fire a
  // telemetry beacon (m.stripe.com) on each mutation, flooding the network.
  const providerTree = useMemo(
    () => (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={{ [MATERIAL_THEME_ID]: theme }}>
          <FinanceableThemeProvider palette={customPalette}>
            <ThemeProvider theme={(joyTheme) => ({ ...theme, $$joy: joyTheme })}>
              <SnackbarProvider maxSnack={3} style={{ height: "150px" }}>
                <LodgeAIProvider>
                  <CssBaseline />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Router />
                    <Snackbar />
                    <IntercomChat />
                  </LocalizationProvider>
                </LodgeAIProvider>
              </SnackbarProvider>
            </ThemeProvider>
          </FinanceableThemeProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    ),
    [theme, customPalette],
  );

  return providerTree;
}
